import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { APIRoutes } from '@/services/api.routes';
import { useUser } from '@/contexts/UserContext';
import { ChatMessage } from '@/components/chat/ChatMessage';
import FullscreenError from '@/components/utils/FullscreenError';
import type { ChatMessage as ChatMessageType } from '@/models/chat-message';
import type { ChatSession } from '@/models';
import {
  faChevronLeft,
  faMessage,
  faSpinner,
  faExclamationTriangle,
  faBug,
  faEye,
  faShieldAlt
} from '@/fontawesome';

export default function AdminChatWindow() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useUser();

  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chatSession, setChatSession] = useState<ChatSession | null>(null);
  const [debugMode, setDebugMode] = useState(false);
  const [criticalError, setCriticalError] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }

    if (sessionId) {
      fetchChatSession();
      fetchMessages();
    }
  }, [sessionId, isAdmin, navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChatSession = async () => {
    if (!sessionId) return;

    try {
      const session = await APIRoutes.getChatSession(sessionId);
      setChatSession(session);
    } catch (err) {
      console.error('Critical error loading chat session:', err);
      setError('Backend nicht erreichbar oder Chat-Sitzung nicht gefunden');
      setCriticalError(true);
      handleAPIError(err);
    }
  };

  const fetchMessages = async () => {
    if (!sessionId) return;

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const messagesResponse = await APIRoutes.listMessages(sessionId, undefined, debugMode);
      setMessages(messagesResponse.messages);
    } catch (err) {
      console.error('Critical error loading messages:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Nachrichten');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleDebugMode = async () => {
    const newDebugMode = !debugMode;
    setDebugMode(newDebugMode);

    if (sessionId) {
      try {
        setLoading(true);
        setError(null);
        setCriticalError(false);
        const messagesResponse = await APIRoutes.listMessages(sessionId, undefined, newDebugMode);
        setMessages(messagesResponse.messages);
      } catch (err) {
        console.error('Critical error loading debug messages:', err);
        setError('Backend nicht erreichbar oder Fehler beim Laden der Debug-Nachrichten');
        setCriticalError(true);
        handleAPIError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const retryFetch = () => {
    fetchChatSession();
    fetchMessages();
  };

  if (!isAdmin) {
    return null;
  }

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Chat nicht verfügbar"
        message={error || 'Der Chat konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar oder die Chat-Sitzung existiert nicht.'}
        onRetry={retryFetch}
        onBack={() => navigate('/admin/users')}
      />
    );
  }

  return (
    <>
      <div className="h-full flex flex-col min-h-0 relative -m-8">
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to="/admin/users"
                className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="w-5 h-5" />
              </Link>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faEye} className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-2" />
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Admin-Ansicht: {chatSession?.title || 'Chat'}
                </h1>
                <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-400">
                  <FontAwesomeIcon icon={faShieldAlt} className="w-3 h-3 mr-1" />
                  Admin
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={toggleDebugMode}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  debugMode
                    ? 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900/70'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                }`}
              >
                <FontAwesomeIcon icon={faBug} className="w-4 h-4 mr-1" />
                {debugMode ? 'Debug: Ein' : 'Debug: Aus'}
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="max-w-4xl mx-auto h-full flex flex-col">
            <div className="flex-1 overflow-y-auto px-4 py-6">
              {loading && messages.length === 0 && (
                <div className="flex justify-center items-center h-full">
                  <div className="text-center">
                    <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">Lade Chat-Nachrichten...</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                    <p className="text-red-600 dark:text-red-400">{error}</p>
                  </div>
                </div>
              )}

              {messages.length === 0 && !loading && !error && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <FontAwesomeIcon icon={faMessage} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 text-lg">Keine Nachrichten in diesem Chat</p>
                  </div>
                </div>
              )}

              {messages.length > 0 && (
                <div className="space-y-6">
                  {messages.map((message) => (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      debugMode={debugMode}
                    />
                  ))}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Read-only notice */}
            <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-4 bg-amber-50 dark:bg-amber-900/20">
              <div className="flex items-center justify-center">
                <FontAwesomeIcon icon={faShieldAlt} className="w-4 h-4 text-amber-600 dark:text-amber-400 mr-2" />
                <p className="text-amber-700 dark:text-amber-300 text-sm font-medium">
                  Sie befinden sich im Admin-Modus. Das Senden von Nachrichten ist deaktiviert.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
